import { version as appVersion } from '../../../package.json';
import snappy from 'snappyjs';

// Prometheus metrics structure
interface PrometheusMetric {
  name: string;
  value: number;
  labels: Record<string, string>;
  timestamp: number;
}

// TimeSeries structure for Prometheus remote write protocol
interface Label {
  name: string;
  value: string;
}

interface Sample {
  value: number;
  timestamp: number;
}

interface TimeSeries {
  labels: Label[];
  samples: Sample[];
}

// Global metrics buffer
let metricsBuffer: PrometheusMetric[] = [];

// Get current user ID from global variable
let currentUserId = 'anonymous';
/**
 *
 * @param userId
 */
export function setCurrentUserId(userId: string): void {
  currentUserId = userId;
}

const getUserId = (): string => currentUserId;

// Generate session ID (persists for the browser session)
const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const getSessionId = (): string => sessionId;

// Helper to generate unique IDs
/**
 *
 * @param prefix
 */
function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Get Prometheus remote write URL (via NestJS proxy)
const getPrometheusUrl = (): string => {
  const apiBaseUrl = import.meta.env.VITE_APP_NESTJS_BASE_URL || '';
  return `${apiBaseUrl}/observability/metric_series/write`;
};

// Check if frontend observability is enabled
const isObservabilityEnabled = (): boolean => {
  const isEnabledByFlag = import.meta.env.VITE_APP_FRONTEND_OBSERVABILITY_ENABLED === 'true';
  const isLocalRuntime =
    import.meta.env.MODE === 'development' ||
    (typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

  return isEnabledByFlag && !isLocalRuntime;
};

// Check if we're in development mode for logging
const isDevelopment = (): boolean => {
  return import.meta.env.VITE_APP_FARO_ENV === 'DEV' || import.meta.env.MODE === 'development';
};

// Conditional logging helpers - only log in development
const devLog = (...args: any[]): void => {
  if (isDevelopment()) {
    console.log(...args);
  }
};

const devError = (...args: any[]): void => {
  if (isDevelopment()) {
    console.error(...args);
  }
};

/**
 * Converts metrics to Prometheus text format
 * @param metrics
 */
function metricsToPrometheusFormat(metrics: PrometheusMetric[]): string {
  const grouped = new Map<string, PrometheusMetric[]>();

  // Group metrics by name
  metrics.forEach((metric) => {
    if (!grouped.has(metric.name)) {
      grouped.set(metric.name, []);
    }
    grouped.get(metric.name)!.push(metric);
  });

  let output = '';

  grouped.forEach((values, name) => {
    // Add help and type comments
    output += `# HELP ${name} \n# TYPE ${name} gauge\n`;

    values.forEach((metric) => {
      // Format labels
      const labelPairs = Object.entries(metric.labels)
        .map(([key, value]) => `${key}="${String(value).replace(/"/g, '\\"')}"`)
        .join(',');

      // Format metric
      output += `${name}{${labelPairs}} ${metric.value} ${metric.timestamp}\n`;
    });

    output += '\n';
  });

  return output;
}

/**
 * Converts metrics to Prometheus remote write format (JSON)
 * @param metrics
 */
function metricsToRemoteWrite(metrics: PrometheusMetric[]): TimeSeries[] {
  const timeSeriesMap = new Map<string, TimeSeries>();

  metrics.forEach((metric) => {
    const labels: Label[] = [
      { name: '__name__', value: metric.name },
      ...Object.entries(metric.labels).map(([k, v]) => ({ name: k, value: String(v) }))
    ];

    const labelKey = labels.map((l) => `${l.name}=${l.value}`).join(',');
    const sample: Sample = {
      value: metric.value,
      timestamp: metric.timestamp
    };

    if (!timeSeriesMap.has(labelKey)) {
      timeSeriesMap.set(labelKey, {
        labels,
        samples: []
      });
    }

    timeSeriesMap.get(labelKey)!.samples.push(sample);
  });

  return Array.from(timeSeriesMap.values());
}

/**
 * Encode varint (variable-length integer)
 * @param value
 */
function encodeVarint(value: number | bigint): number[] {
  const bytes: number[] = [];
  let num = typeof value === 'bigint' ? value : BigInt(value);

  while (num > 0n) {
    let byte = Number(num & 0x7fn);
    num >>= 7n;
    if (num > 0n) {
      byte |= 0x80;
    }
    bytes.push(byte);
  }

  return bytes.length > 0 ? bytes : [0];
}

/**
 * Encodes Prometheus WriteRequest to protobuf format
 * @param timeSeries
 */
function encodeWriteRequest(timeSeries: TimeSeries[]): Uint8Array {
  const chunks: number[] = [];

  timeSeries.forEach((ts) => {
    const tsChunks: number[] = [];

    // Labels (field 1, repeated, each label is a nested message)
    ts.labels.forEach((label) => {
      const labelChunks: number[] = [];

      // name (field 1, string)
      const nameBytes = new TextEncoder().encode(label.name);
      labelChunks.push(0x0a); // field 1, wire type 2
      labelChunks.push(...encodeVarint(nameBytes.length));
      labelChunks.push(...Array.from(nameBytes));

      // value (field 2, string)
      const valueBytes = new TextEncoder().encode(label.value);
      labelChunks.push(0x12); // field 2, wire type 2
      labelChunks.push(...encodeVarint(valueBytes.length));
      labelChunks.push(...Array.from(valueBytes));

      // Wrap label in length-delimited field
      tsChunks.push(0x0a); // field 1, wire type 2
      tsChunks.push(...encodeVarint(labelChunks.length));
      tsChunks.push(...labelChunks);
    });

    // Samples (field 2, repeated, each sample is a nested message)
    ts.samples.forEach((sample) => {
      const sampleChunks: number[] = [];

      // value (field 1, double - wire type 1, fixed64)
      const valueBytes = new Float64Array([sample.value]);
      sampleChunks.push(0x09); // field 1, wire type 1
      sampleChunks.push(...Array.from(new Uint8Array(valueBytes.buffer)));

      // timestamp (field 2, int64 - wire type 0, varint)
      sampleChunks.push(0x10); // field 2, wire type 0
      sampleChunks.push(...encodeVarint(BigInt(sample.timestamp)));

      // Wrap sample in length-delimited field
      tsChunks.push(0x12); // field 2, wire type 2
      tsChunks.push(...encodeVarint(sampleChunks.length));
      tsChunks.push(...sampleChunks);
    });

    // Wrap timeseries in length-delimited field
    chunks.push(0x0a); // field 1, wire type 2
    chunks.push(...encodeVarint(tsChunks.length));
    chunks.push(...tsChunks);
  });

  return new Uint8Array(chunks);
}

/**
 * Pushes metrics to Prometheus via remote write
 */
async function pushMetricsToPrometheus(): Promise<void> {
  // Check if observability is enabled, if not, skip pushing
  if (!isObservabilityEnabled()) {
    return;
  }
  if (metricsBuffer.length === 0) return;

  const metricsToSend = [...metricsBuffer];
  metricsBuffer = []; // Clear buffer after copying

  devLog(`🚀 Pushing ${metricsToSend.length} metrics to Prometheus...`);
  devLog(
    '📊 Metrics to send:',
    metricsToSend.map((m) => ({ name: m.name, labels: m.labels }))
  );

  try {
    const remoteWriteUrl = getPrometheusUrl();

    devLog('🔗 Prometheus URL:', remoteWriteUrl);

    // Convert to Prometheus remote write format
    const timeSeries = metricsToRemoteWrite(metricsToSend);

    // Encode to protobuf
    const protobufData = encodeWriteRequest(timeSeries);
    devLog('📦 Protobuf data length:', protobufData.length, 'bytes');
    devLog('📊 TimeSeries count:', timeSeries.length);

    // Compress with snappy (snappyjs works in browser)
    const compressedData = snappy.compress(protobufData);
    devLog('🗜️ Compressed data length:', compressedData.length, 'bytes');

    // Convert to standard Uint8Array for Blob
    const uint8Array = new Uint8Array(compressedData);

    // Wrap in Blob for fetch
    const blob = new Blob([uint8Array], { type: 'application/x-protobuf' });

    const response = await fetch(remoteWriteUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-protobuf',
        'Content-Encoding': 'snappy',
        'X-Prometheus-Remote-Write-Version': '0.1.0'
      },
      body: blob,
      keepalive: true
    });

    if (!response.ok) {
      devError('❌ Prometheus push failed:', response.status, response.statusText);
      const responseText = await response.text();
      devError('❌ Response:', responseText);

      // Only re-queue on transient errors (5xx, network issues)
      // Don't retry on 4xx errors (bad request, malformed data)
      if (response.status >= 500) {
        devError('⚠️ Server error, re-queuing metrics for retry');
        metricsBuffer.push(...metricsToSend);
      } else {
        devError('❌ Client error (4xx), dropping metrics (not retrying bad data)');
      }
    } else {
      devLog('✅ Prometheus push successful!');
    }
  } catch (err) {
    devError('❌ Prometheus push error:', err);
    // Re-queue on network/connection errors (transient)
    devError('⚠️ Network error, re-queuing metrics for retry');
    metricsBuffer.push(...metricsToSend);
  }
}

/**
 * Adds a metric to the buffer
 * @param name
 * @param value
 * @param labels
 */
function addMetric(name: string, value: number, labels: Record<string, string> = {}): void {
  // Check if observability is enabled, if not, skip adding metrics
  if (!isObservabilityEnabled()) {
    return;
  }

  const defaultLabels = {
    environment: import.meta.env.VITE_APP_ENV || 'development',
    application: import.meta.env.VITE_APP_FARO_APPLICATION || 'MainSpa',
    app_version: appVersion,
    user_id: getUserId(),
    session_id: getSessionId()
  };

  const metric: PrometheusMetric = {
    name: `frontend_${name}`,
    value,
    labels: { ...defaultLabels, ...labels },
    timestamp: Date.now()
  };

  metricsBuffer.push(metric);
  devLog(`📈 Metric added to buffer: ${metric.name}, buffer size: ${metricsBuffer.length}`, metric);

  // Push immediately if buffer is getting large
  if (metricsBuffer.length >= 50) {
    devLog('🚀 Buffer full (50 metrics), pushing to Prometheus...');
    void pushMetricsToPrometheus();
  }
}

/**
 * Flush metrics on a regular interval
 */
setInterval(() => {
  if (isObservabilityEnabled() && metricsBuffer.length > 0) {
    devLog(`⏰ Interval flush triggered, buffer size: ${metricsBuffer.length}`);
    void pushMetricsToPrometheus();
  }
}, 5000); // Push every 5 seconds

/**
 * Metric functions for different use cases
 */

// ====================
// User Metrics
// ====================

/**
 *
 */
export function trackLoginSuccess(): void {
  const loginId = generateId('login');
  addMetric('user_login_success', 1, { status: 'success', login_id: loginId });
}

/**
 *
 * @param reason
 */
export function trackLoginFailure(reason: string): void {
  const loginId = generateId('login');
  addMetric('user_login_failure', 1, { reason, status: 'failure', login_id: loginId });
}

/**
 *
 */
export function trackRegistrationSuccess(): void {
  const registrationId = generateId('registration');
  addMetric('user_registration_success', 1, { status: 'success', registration_id: registrationId });
}

/**
 *
 * @param reason
 */
export function trackRegistrationFailure(reason: string): void {
  const registrationId = generateId('registration');
  addMetric('user_registration_failure', 1, { reason, status: 'failure', registration_id: registrationId });
}

/**
 *
 */
export function trackDailyActiveUser(): void {
  addMetric('user_daily_active', 1, { date: new Date().toISOString().split('T')[0] });
}

// ====================
// Page View Metrics
// ====================

/**
 *
 * @param pagePath
 * @param pageTitle
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  const pageViewId = generateId('pageview');
  const labels: Record<string, string> = {
    page_path: pagePath,
    page_url: window.location.href,
    browser_name: (navigator as any).userAgentData?.brands?.[0]?.brand || navigator.userAgent,
    pageview_id: pageViewId
  };

  if (pageTitle) {
    labels.page_title = pageTitle;
  }

  addMetric('page_view', 1, labels);

  // Track DAU on page view
  trackDailyActiveUser();
}

// ====================
// User Interaction Metrics
// ====================

/**
 *
 * @param pagePath
 * @param elementType
 * @param elementId
 * @param elementLabel
 */
export function trackUserClick(pagePath: string, elementType: string, elementId?: string, elementLabel?: string): void {
  const clickId = generateId('click');
  const labels: Record<string, string> = {
    page_path: pagePath,
    element_type: elementType,
    element_id: elementId || 'none',
    click_id: clickId
  };

  if (elementLabel) {
    labels.element_label = elementLabel;
  }

  addMetric('user_click', 1, labels);
  devLog('📊 User click tracked:', { pagePath, elementType, elementId, elementLabel, clickId });
}

/**
 *
 * @param pagePath
 * @param formName
 */
export function trackFormSubmit(pagePath: string, formName: string): void {
  const formId = generateId('form');
  addMetric('form_submission', 1, {
    page_path: pagePath,
    form_name: formName,
    form_submission_id: formId
  });
}

/**
 *
 * @param pagePath
 * @param buttonId
 * @param buttonLabel
 */
export function trackButtonClick(pagePath: string, buttonId: string, buttonLabel?: string): void {
  const clickId = generateId('button');
  const labels: Record<string, string> = {
    page_path: pagePath,
    button_id: buttonId,
    click_id: clickId
  };

  if (buttonLabel) {
    labels.button_label = buttonLabel;
  }

  addMetric('button_click', 1, labels);
  devLog('📊 Button click tracked:', { pagePath, buttonId, buttonLabel, clickId });
}

// ====================
// Contract Metrics
// ====================

/**
 *
 * @param pagePath
 * @param contractId
 * @param contractType
 */
export function trackContractCreated(pagePath: string, contractId: string, contractType?: string): void {
  addMetric('contract_created', 1, {
    page_path: pagePath,
    contract_type: contractType || 'unknown',
    contract_id: contractId
  });
}

/**
 *
 * @param pagePath
 * @param contractId
 * @param contractType
 */
export function trackContractApproved(pagePath: string, contractId: string, contractType?: string): void {
  addMetric('contract_approved', 1, {
    page_path: pagePath,
    contract_type: contractType || 'unknown',
    contract_id: contractId
  });
}

/**
 *
 * @param pagePath
 * @param contractId
 * @param reason
 */
export function trackContractRejected(pagePath: string, contractId: string, reason?: string): void {
  addMetric('contract_rejected', 1, {
    page_path: pagePath,
    reason: reason || 'unknown',
    contract_id: contractId
  });
}

/**
 *
 * @param pagePath
 * @param documentId
 * @param processingTimeMs
 */
export function trackDocumentProcessingTime(pagePath: string, documentId: string, processingTimeMs: number): void {
  addMetric('document_processing_duration_seconds', processingTimeMs / 1000, {
    page_path: pagePath,
    document_id: documentId
  });
}

/**
 *
 * @param pagePath
 * @param documentId
 * @param errorType
 */
export function trackDocumentValidationError(pagePath: string, documentId: string, errorType: string): void {
  addMetric('document_validation_error', 1, {
    page_path: pagePath,
    error_type: errorType,
    document_id: documentId
  });
}

const DOCUMENT_VIEWER_SOURCE_QUERY_PARAM = 'from';

/**
 * Appends the originating SPA path to a standalone document viewer URL.
 * Required because `window.open(..., 'noopener,noreferrer')` blocks referrer and
 * does not share sessionStorage with the opener tab.
 * @param documentViewerPath
 * @param sourcePage
 */
export function buildStandaloneDocumentViewerUrl(
  documentViewerPath: string,
  sourcePage: string = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
): string {
  const separator = documentViewerPath.includes('?') ? '&' : '?';
  return `${documentViewerPath}${separator}${DOCUMENT_VIEWER_SOURCE_QUERY_PARAM}=${encodeURIComponent(sourcePage)}`;
}

/**
 * Reads the originating SPA path from a standalone document viewer URL query string.
 * @param search
 */
export function parseDocumentViewerSourcePage(search: string): string | undefined {
  const from = new URLSearchParams(search).get(DOCUMENT_VIEWER_SOURCE_QUERY_PARAM);
  return from ? decodeURIComponent(from) : undefined;
}

export interface DocumentViewerTracking {
  sourcePage?: string;
  documentId?: string;
  viewerContext?: string;
}

export interface DocumentViewerOpenedParams extends DocumentViewerTracking {
  documentName: string;
  documentFormat?: string;
}

/**
 * Tracks when a user opens a document in the Apryse viewer.
 * @param params
 */
export function trackDocumentViewerOpened(params: DocumentViewerOpenedParams): void {
  const openId = generateId('doc_open');
  const documentFormat = params.documentFormat || params.documentName.split('.').pop()?.toLowerCase() || 'unknown';
  const sourcePage = params.sourcePage || resolveDocumentViewerSourcePage();

  addMetric('document_viewer_opened', 1, {
    page_path: sourcePage,
    document_name: params.documentName,
    document_id: params.documentId || 'unknown',
    document_format: documentFormat,
    viewer_context: resolveDocumentViewerContext(params.viewerContext),
    document_open_id: openId
  });
  devLog('📄 Document viewer opened:', { ...params, sourcePage, openId });
}

/**
 * Resolves the page the user was on when opening a document in an embedded Apryse viewer.
 */
export function resolveDocumentViewerSourcePage(): string {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  return window.location.pathname;
}

/**
 * Resolves how the Apryse viewer is hosted in the app.
 */
export function resolveDocumentViewerContext(explicitContext?: string): string {
  if (explicitContext) {
    return explicitContext;
  }

  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/document-viewer')) {
    return 'standalone';
  }

  return 'embedded';
}

// ====================
// Chat Metrics
// ====================

/**
 *
 * @param pagePath
 * @param chatId
 * @param messageId
 */
export function trackChatMessageSent(pagePath: string, chatId: string, messageId: string): void {
  addMetric('chat_message_sent', 1, {
    page_path: pagePath,
    chat_id: chatId,
    message_id: messageId
  });
}

/**
 *
 * @param pagePath
 * @param chatId
 * @param messageId
 * @param success
 */
export function trackChatMessageDelivered(pagePath: string, chatId: string, messageId: string, success: boolean): void {
  const status = success ? 'success' : 'failure';
  addMetric('chat_message_delivery', success ? 1 : 0, {
    page_path: pagePath,
    status,
    chat_id: chatId,
    message_id: messageId
  });
}

/**
 *
 * @param pagePath
 * @param chatId
 * @param connected
 */
export function trackChatConnectionActive(pagePath: string, chatId: string, connected: boolean): void {
  const value = connected ? 1 : 0;
  addMetric('chat_connection_active', value, {
    page_path: pagePath,
    chat_id: chatId
  });
}

/**
 *
 * @param pagePath
 * @param roomCount
 */
export function trackChatActiveRooms(pagePath: string, roomCount: number): void {
  addMetric('chat_active_rooms', roomCount, {
    page_path: pagePath
  });
}

// ====================
// Feature Usage Metrics
// ====================

/**
 *
 * @param featureName
 */
export function trackFeatureUsage(featureName: string): void {
  addMetric('feature_usage', 1, {
    feature_name: featureName
  });
}

/**
 *
 * @param moduleName
 */
export function trackModuleUsage(moduleName: string): void {
  addMetric('module_usage', 1, {
    module_name: moduleName
  });
}

// ====================
// Performance Metrics (Duration metrics are kept as they provide actual values)
// ====================

/**
 *
 * @param pagePath
 * @param loadTimeMs
 */
export function trackPageLoadTime(pagePath: string, loadTimeMs: number): void {
  const loadId = generateId('pageload');
  addMetric('page_load_duration_seconds', loadTimeMs / 1000, {
    page_path: pagePath,
    load_id: loadId
  });
}

/**
 *
 * @param endpoint
 * @param method
 * @param durationMs
 */
export function trackAPICallDuration(endpoint: string, method: string, durationMs: number): void {
  const callId = generateId('apicall');
  addMetric('api_call_duration_seconds', durationMs / 1000, {
    endpoint,
    method,
    call_id: callId
  });
}

// ====================
// Error Tracking (for non-exception tracking)
// ====================

/**
 *
 * @param errorType
 * @param errorMessage
 * @param pagePath
 */
export function trackError(errorType: string, errorMessage: string, pagePath: string): void {
  const errorId = generateId('error');
  addMetric('error', 1, {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit message length
    page_path: pagePath,
    error_id: errorId
  });
}

// Export function to manually flush metrics
/**
 *
 */
export function flushMetrics(): Promise<void> {
  return pushMetricsToPrometheus();
}

// ====================
// Global Interaction Tracking
// ====================

/**
 * Extract button/element information for tracking
 * @param {HTMLElement} element - The DOM element from which tracking metadata is extracted.
 */
function getElementTrackingInfo(element: HTMLElement): {
  id: string;
  label: string;
  type: string;
} {
  // Check for data attributes first (highest priority)
  const dataTrackingId = element.getAttribute('data-tracking-id');
  const dataTrackingLabel = element.getAttribute('data-tracking-label');

  // Get element type
  const tagName = element.tagName.toLowerCase();
  let elementType = tagName;

  // Determine if it's an Ant Design button or regular button
  if (tagName === 'button') {
    const antButtonClass = element.className.includes('ant-btn');
    elementType = antButtonClass ? 'ant-button' : 'button';
  } else if (tagName === 'a') {
    elementType = 'link';
  } else if (element.getAttribute('role') === 'button') {
    elementType = 'button-role';
  }

  // Try to extract button ID
  let buttonId = dataTrackingId || element.id;

  // If no ID, generate from text content or other attributes
  if (!buttonId) {
    const ariaLabel = element.getAttribute('aria-label');
    const title = element.getAttribute('title');
    const textContent = element.textContent?.trim() || '';

    // Use aria-label or title if available
    if (ariaLabel) {
      buttonId = ariaLabel
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    } else if (title) {
      buttonId = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    } else if (textContent && textContent.length > 0 && textContent.length < 50) {
      buttonId = textContent
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    } else {
      // Fallback to element type + random suffix
      buttonId = `${elementType}-${Math.random().toString(36).substr(2, 6)}`;
    }
  }

  // Try to extract button label
  let buttonLabel = dataTrackingLabel;

  if (!buttonLabel) {
    const ariaLabel = element.getAttribute('aria-label');
    const title = element.getAttribute('title');
    const textContent = element.textContent?.trim() || '';

    // Use aria-label, title, or text content
    buttonLabel = ariaLabel || title || textContent || buttonId;
  }

  // Limit label length
  if (buttonLabel && buttonLabel.length > 100) {
    buttonLabel = buttonLabel.substring(0, 100) + '...';
  }

  return {
    id: buttonId,
    label: buttonLabel || 'unknown',
    type: elementType
  };
}

/**
 * Check if an element or its parents should be tracked
 * @param {HTMLElement | null} element - The initial target element from the interaction event.
 */
function findTrackableElement(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  // Check up to 5 levels up for trackable elements
  let current: HTMLElement | null = element;
  let depth = 0;

  while (current && depth < 5) {
    const tagName = current.tagName.toLowerCase();
    const hasTrackingAttr = current.hasAttribute('data-tracking-id') || current.hasAttribute('data-track');
    const isButton = tagName === 'button' || current.getAttribute('role') === 'button';
    const isLink = tagName === 'a';
    const isAntButton = current.className.includes('ant-btn');
    const isInput =
      tagName === 'input' && (current.getAttribute('type') === 'button' || current.getAttribute('type') === 'submit');

    // Track if:
    // 1. Has explicit tracking attributes
    // 2. Is a button (any type)
    // 3. Is an Ant Design button
    // 4. Is a link
    // 5. Is a submit/button input
    if (hasTrackingAttr || isButton || isLink || isAntButton || isInput) {
      return current;
    }

    current = current.parentElement;
    depth++;
  }

  return null;
}

/**
 * Global click handler for automatic tracking
 * @param {MouseEvent} event - The browser click event.
 */
function handleGlobalClick(event: MouseEvent): void {
  try {
    const target = event.target as HTMLElement;
    const trackableElement = findTrackableElement(target);

    if (!trackableElement) {
      return; // Not a trackable element
    }

    // Check if tracking is explicitly disabled
    if (trackableElement.hasAttribute('data-no-tracking') || trackableElement.hasAttribute('data-tracking-disabled')) {
      return;
    }

    const { id, label, type } = getElementTrackingInfo(trackableElement);
    const pagePath = window.location.pathname;

    // Track based on element type
    if (type.includes('button') || type === 'ant-button') {
      trackButtonClick(pagePath, id, label);
      devLog('🎯 Auto-tracked button click:', { id, label, type, pagePath });
    } else if (type === 'link') {
      trackUserClick(pagePath, 'link', id, label);
      devLog('🎯 Auto-tracked link click:', { id, label, pagePath });
    } else {
      trackUserClick(pagePath, type, id, label);
      devLog('🎯 Auto-tracked element click:', { id, label, type, pagePath });
    }
  } catch (error) {
    devError('❌ Error in global click tracking:', error);
  }
}

/**
 * Global form submit handler for automatic tracking
 * @param {SubmitEvent} event - The browser form submit event.
 */
function handleGlobalSubmit(event: SubmitEvent): void {
  try {
    const form = event.target as HTMLFormElement;

    // Check if tracking is explicitly disabled
    if (form.hasAttribute('data-no-tracking') || form.hasAttribute('data-tracking-disabled')) {
      return;
    }

    // Get form name/id
    const formName =
      form.getAttribute('data-tracking-id') ||
      form.getAttribute('name') ||
      form.id ||
      'form-' + Math.random().toString(36).substr(2, 9);

    const pagePath = window.location.pathname;

    trackFormSubmit(pagePath, formName);
    devLog('🎯 Auto-tracked form submission:', { formName, pagePath });
  } catch (error) {
    devError('❌ Error in global form tracking:', error);
  }
}

/**
 * No-op cleanup function for when tracking is already enabled
 */
function noopCleanup(): void {
  // Intentionally empty - tracking is already set up
}

/**
 * Setup global interaction tracking
 * Returns cleanup function to remove listeners
 * @returns Cleanup function
 * @example
 * ```tsx
 * // In a component or hook
 * useEffect(() => {
 *   const cleanup = setupGlobalInteractionTracking();
 *   return cleanup;
 * }, []);
 * ```
 */
let globalTrackingEnabled = false;

/**
 * Sets up global interaction tracking for user events
 * Attaches event listeners to track clicks, form submissions, and navigation
 * @returns {() => void} Cleanup function to remove event listeners
 * @example
 * ```typescript
 * // In a component
 * useEffect(() => {
 *   const cleanup = setupGlobalInteractionTracking();
 *   return cleanup; // Clean up on unmount
 * }, []);
 * ```
 */
export function setupGlobalInteractionTracking(): () => void {
  // Check if frontend observability is enabled
  if (!isObservabilityEnabled()) {
    // console.log('🚫 Frontend observability is disabled (VITE_APP_FRONTEND_OBSERVABILITY_ENABLED is not "true")');
    // Return a no-op cleanup function
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }
  // Prevent multiple setups
  if (globalTrackingEnabled) {
    devLog('⚠️ Global interaction tracking already enabled');
    return noopCleanup;
  }

  devLog('✅ Setting up global interaction tracking');

  // Add click listener with capture phase to ensure we catch all clicks
  document.addEventListener('click', handleGlobalClick, { capture: true });

  // Add form submit listener
  document.addEventListener('submit', handleGlobalSubmit, { capture: true });

  globalTrackingEnabled = true;

  // Return cleanup function
  return () => {
    devLog('🧹 Cleaning up global interaction tracking');
    document.removeEventListener('click', handleGlobalClick, { capture: true });
    document.removeEventListener('submit', handleGlobalSubmit, { capture: true });
    globalTrackingEnabled = false;
  };
}
